"use client";
import { Track } from "@/src/lib/tracking";

import {
  isAppAuxiliaryShellRoute,
  isLoginPopupRoute,
  isLoginRoute,
  isNewsRoute,
  isOnboardingRoute,
  isPricingRoute,
  isWelcomeRoute,
} from "./isOnboardingRoute";
import {
  isOwnerProfileReadyForEntryGate,
  ownerNeedsWelcomeNickName,
} from "./ownerWelcomeGate";
import { useEffect, useRef } from "react";

import { ROUTER_PATH } from "@config/router";
import { rootStore } from "@store/rootStore";
import { useMemoizedFn } from "ahooks";
import { usePathname } from "@/src/hooks/usePathname";
import { useShellRouter } from "@/src/hooks/useShellAwareRouter";
import { useSnapshot } from "valtio";
import { preparePlanGuideOnboardingEntry } from "@hooks/plan";
import { useChatOnboardingApi } from "./useChatOnboardingApi";
import { clearQuizResultContext } from "./quizResultContextStorage";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import {
  getAdSourceCookie,
  setAdSourceCookie,
  setAdSourceInStore,
  setEdgenOnboardingStepCookie,
  shouldAdSkipOnboarding,
  trackAdLandHome,
} from "@utils/onboarding";
import { isUnifiedNativeMainShell } from "@/src/lib/bridge";
import { isInNativeDetailShell } from "@/src/lib/bridge/shellAppNavigation";
import { deviceDetector } from "utils";
import { useOnboardingHarnessQuery } from "./useOnboardingHarnessQuery";

/**
 * 从 URL 中解析并验证 returnUrl（防止 open redirect）。
 * 只接受相对路径，拒绝协议链接和指向登录页的循环引用。
 */
function getValidReturnUrl(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("returnUrl");
  if (!raw) {
    return null;
  }
  if (raw.startsWith("//") || raw.includes("://")) {
    return null;
  }
  if (isLoginRoute(raw)) {
    return null;
  }
  return raw;
}

export interface UseOnboardingEntryGateOptions {
  /** 跳转 onboarding 前关闭登录 Modal */
  closeLoginModal?: () => void;
}

/**
 * V3 entry gate（D3 / D13）：
 * 1. `/welcome` — 307 等价 redirect `/onboarding`（遗留书签）
 * 2. `/pricing`、`/news` — 不做任何跳转
 * 3. 其他已登录页 — 无 `nick_name` 不调 `onboarding-status`，直接 cookie `2` + `/onboarding`；
 *    有 nick 再调 status 分流
 * 4. `/onboarding` 守卫 — 无 nick 放行 UI3/UI4；有 nick 且 `completed` 时 redirect brief
 */
export function useOnboardingEntryGate(
  options: UseOnboardingEntryGateOptions = {},
): void {
  const isApp = deviceDetector.isApp();
  const { closeLoginModal } = options;
  const router = useShellRouter();
  const pathname = usePathname();
  const onboardingApi = useChatOnboardingApi();
  const { isLoggedIn, isFetching } = useSnapshot(rootStore.login);
  const { ownerInfo, profileState } = useSnapshot(rootStore.user);
  const onboardingEntryGateCheckedRef = useRef(false);
  const checkInFlightRef = useRef(false);
  const { chatApi } = useOspApiClientProvider();
  const harness = useOnboardingHarnessQuery();

  const navigateToOnboarding = useMemoizedFn(() => {
    if (isInNativeDetailShell()) {
      return;
    }
    if (!isUnifiedNativeMainShell() && isApp) {
      return;
    }
    closeLoginModal?.();
    setEdgenOnboardingStepCookie(2);
    Track.log("info", "useOnboardingEntryGate", "navigating to onboarding", {
      pathname,
    });
    preparePlanGuideOnboardingEntry();
    router.replace(ROUTER_PATH.ONBOARDING);
  });

  const runWelcomeRedirect = useMemoizedFn(async () => {
    if (checkInFlightRef.current || !isWelcomeRoute(pathname)) {
      return;
    }
    if (!isLoggedIn || isFetching) {
      return;
    }
    if (!isOwnerProfileReadyForEntryGate(profileState)) {
      return;
    }

    checkInFlightRef.current = true;
    try {
      Track.log(
        "info",
        "useOnboardingEntryGate",
        "legacy /welcome → /onboarding",
        {
          pathname,
        },
      );
      closeLoginModal?.();
      setEdgenOnboardingStepCookie(2);
      router.replace(ROUTER_PATH.ONBOARDING);
    } finally {
      checkInFlightRef.current = false;
    }
  });

  const runOnboardingPageGuard = useMemoizedFn(async () => {
    if (checkInFlightRef.current || !isOnboardingRoute(pathname)) {
      return;
    }
    if (harness.enabled && harness.auth === "onboarding") {
      return;
    }
    if (harness.enabled && harness.auth === "completed") {
      checkInFlightRef.current = true;
      try {
        setEdgenOnboardingStepCookie(3);
        router.replace(ROUTER_PATH.BRIEF);
      } finally {
        checkInFlightRef.current = false;
      }
      return;
    }
    if (!isLoggedIn || isFetching) {
      return;
    }
    if (!isOwnerProfileReadyForEntryGate(profileState)) {
      return;
    }

    if (ownerNeedsWelcomeNickName(ownerInfo)) {
      checkInFlightRef.current = true;
      try {
        setEdgenOnboardingStepCookie(2);
        Track.log(
          "info",
          "useOnboardingEntryGate",
          "onboarding page allowed without nick — skip status API",
          { pathname },
        );
      } finally {
        checkInFlightRef.current = false;
      }
      return;
    }

    const { shouldEnter, onboardingStage } =
      await onboardingApi.getOnboardingStatus();
    checkInFlightRef.current = true;
    try {
      if (shouldEnter) {
        setEdgenOnboardingStepCookie(2);
        Track.log(
          "info",
          "useOnboardingEntryGate",
          "onboarding page access allowed",
          {
            onboardingStage,
            pathname,
          },
        );
        return;
      }

      setEdgenOnboardingStepCookie(3);
      Track.log(
        "info",
        "useOnboardingEntryGate",
        "redirect from onboarding to brief",
        {
          onboardingStage,
          pathname,
          isLoggedIn,
        },
      );
      clearQuizResultContext("skip-onboarding-entry-gate");
      router.replace(ROUTER_PATH.BRIEF);
    } finally {
      checkInFlightRef.current = false;
    }
  });

  const runEntryGate = useMemoizedFn(async () => {
    if (checkInFlightRef.current) {
      return;
    }
    if (!isLoggedIn || isFetching) {
      return;
    }
    if (isInNativeDetailShell()) {
      onboardingEntryGateCheckedRef.current = true;
      return;
    }
    if (isLoginPopupRoute(pathname)) {
      onboardingEntryGateCheckedRef.current = true;
      return;
    }
    if (!isOwnerProfileReadyForEntryGate(profileState)) {
      return;
    }

    // 后端 ad_source 覆盖本地 cookie + 内存 store（每次都跑，不依赖 checkedRef）
    // 原因：fetchUserInfoFromCache 先恢复（缓存可能无 track_meta.ad_source），
    // fetchUserInfo 远程稍后才返回真实值 → 此时 checkedRef 已 true 但 ad_source 需更新。
    const backendAdSource = ownerInfo?.track_meta?.ad_source?.trim();
    if (backendAdSource) {
      setAdSourceCookie(backendAdSource);
      setAdSourceInStore(backendAdSource);
    }

    if (onboardingEntryGateCheckedRef.current) {
      return;
    }

    // 广告钩子来源（alpha_pick / portfolio_check）：跳过 onboarding，直落 Brief
    if (shouldAdSkipOnboarding()) {
      const adSource = getAdSourceCookie();
      checkInFlightRef.current = true;
      try {
        onboardingEntryGateCheckedRef.current = true;
        setEdgenOnboardingStepCookie(3);
        clearQuizResultContext("ad-source-skip-onboarding");
        closeLoginModal?.();
        Track.log(
          "info",
          "useOnboardingEntryGate",
          "ad-source skip onboarding → brief",
          {
            pathname,
            adSource,
          },
        );
        trackAdLandHome(adSource);
        router.replace(ROUTER_PATH.HOME);
      } finally {
        checkInFlightRef.current = false;
      }
      return;
    }

    if (ownerNeedsWelcomeNickName(ownerInfo)) {
      checkInFlightRef.current = true;
      try {
        onboardingEntryGateCheckedRef.current = true;
        Track.log(
          "info",
          "useOnboardingEntryGate",
          "no nick_name — skip onboarding-status, enter wizard",
          { pathname },
        );
        navigateToOnboarding();
      } finally {
        checkInFlightRef.current = false;
      }
      return;
    }

    const { shouldEnter, onboardingStage } =
      await onboardingApi.getOnboardingStatus();
    checkInFlightRef.current = true;
    try {
      onboardingEntryGateCheckedRef.current = true;

      if (!shouldEnter) {
        setEdgenOnboardingStepCookie(3);
        clearQuizResultContext("skip-onboarding-entry-gate");
        if (isLoginRoute(pathname)) {
          const returnUrl = getValidReturnUrl();
          const target = returnUrl ?? ROUTER_PATH.HOME;
          Track.log(
            "info",
            "useOnboardingEntryGate",
            "redirect from login — onboarding completed",
            {
              onboardingStage,
              pathname,
              isLoggedIn,
              returnUrl,
              target,
            },
          );
          router.replace(target);
        } else {
          Track.log(
            "info",
            "useOnboardingEntryGate",
            "skip onboarding route — onboarding completed",
            {
              onboardingStage,
              pathname,
              isLoggedIn,
            },
          );
        }
        return;
      }

      navigateToOnboarding();
    } finally {
      checkInFlightRef.current = false;
    }
  });

  useEffect(() => {
    if (isFetching || !chatApi) {
      return;
    }

    if (!isLoggedIn) {
      onboardingEntryGateCheckedRef.current = false;
      if (
        harness.enabled &&
        harness.auth === "onboarding" &&
        isOnboardingRoute(pathname)
      ) {
        Track.log(
          "info",
          "useOnboardingEntryGate",
          "harness onboarding — skip guest redirect",
          {
            pathname,
          },
        );
        return;
      }
      if (isOnboardingRoute(pathname) || isWelcomeRoute(pathname)) {
        Track.log(
          "info",
          "useOnboardingEntryGate",
          "unauthenticated immersive route access",
          { pathname },
        );
        router.replace(ROUTER_PATH.BRIEF);
      }
      return;
    }

    if (isPricingRoute(pathname) || isNewsRoute(pathname)) {
      Track.log(
        "info",
        "useOnboardingEntryGate",
        "public content page, skip all redirects",
        { pathname },
      );
      return;
    }

    if (isAppAuxiliaryShellRoute(pathname)) {
      Track.log(
        "info",
        "useOnboardingEntryGate",
        "app auxiliary shell route, skip entry gate",
        { pathname },
      );
      return;
    }

    if (isWelcomeRoute(pathname)) {
      void runWelcomeRedirect();
      return;
    }

    if (isOnboardingRoute(pathname)) {
      void runOnboardingPageGuard();
      return;
    }

    void runEntryGate();
  }, [
    isLoggedIn,
    isFetching,
    pathname,
    profileState,
    ownerInfo?.nick_name,
    router,
    runEntryGate,
    chatApi,
    runOnboardingPageGuard,
    runWelcomeRedirect,
    harness.auth,
    harness.enabled,
  ]);
}
